import React from "react";
import DatePicker from "react-datepicker";

export interface DateInterval {
  start: Date;
  end: Date;
}

export interface DateTimeInputProps {
  value: any[];
  onChange: (values: any[]) => void;
  isSummary?: boolean;
  isClearable?: boolean;
  dateTimeType?: string;
  maxDate?: Date | null;
  minDate?: Date | null;
  excludeDates?: Date[] | undefined;
  excludeDateIntervals?: DateInterval[] | undefined;
  isDateTimeUTC?: boolean;
}

function padStart(n: number): string {
  return n.toString().padStart(2, "0");
}

function padYear(n: number): string {
  return n.toString().padStart(4, "0");
}

export default function DateTimeInput({
  value,
  onChange,
  isSummary = false,
  isClearable = true,
  dateTimeType = "",
  maxDate = null,
  minDate = null,
  excludeDates = undefined,
  excludeDateIntervals = undefined,
  isDateTimeUTC = true,
}: DateTimeInputProps) {
  const selected = (() => {
    if (!value[0] || isNaN(new Date(value[0]).getTime())) return null;
    if (dateTimeType === "time") {
      return isDateTimeUTC
        ? new Date(new Date().setUTCHours(...(value[0].split(":") as [any, any])))
        : new Date(new Date().setHours(...(value[0].split(":") as [any, any])));
    }
    return isDateTimeUTC ? new Date(`${value[0]}Z`) : new Date(value[0]);
  })();

  const handleChange = (date: Date | null) => {
    if (date === null) {
      onChange([null]);
      return;
    }

    let dateValue: string | false;

    if (isDateTimeUTC) {
      dateValue =
        (dateTimeType === "dateTime" &&
          `${padYear(date.getUTCFullYear())}-${padStart(date.getUTCMonth() + 1)}-${padStart(date.getUTCDate())} ${padStart(date.getUTCHours())}:${padStart(date.getUTCMinutes())}`) ||
        (dateTimeType === "date" &&
          `${padYear(date.getFullYear())}-${padStart(date.getMonth() + 1)}-${padStart(date.getDate())}`) ||
        (dateTimeType === "time" &&
          `${padStart(date.getUTCHours())}:${padStart(date.getUTCMinutes())}`);
    } else {
      dateValue =
        (dateTimeType === "dateTime" &&
          `${padYear(date.getFullYear())}-${padStart(date.getMonth() + 1)}-${padStart(date.getDate())} ${padStart(date.getHours())}:${padStart(date.getMinutes())}`) ||
        (dateTimeType === "date" &&
          `${padYear(date.getFullYear())}-${padStart(date.getMonth() + 1)}-${padStart(date.getDate())}`) ||
        (dateTimeType === "time" &&
          `${padStart(date.getHours())}:${padStart(date.getMinutes())}`);
    }

    onChange([dateValue]);
  };

  return (
    <DatePicker
      shouldCloseOnSelect
      disabled={isSummary}
      isClearable={isClearable}
      className="form-control w-100"
      showTimeSelect={dateTimeType === "dateTime" || dateTimeType === "time"}
      showTimeSelectOnly={dateTimeType === "time"}
      timeIntervals={15}
      dateFormat={
        (dateTimeType === "dateTime" && "yyyy-MM-dd HH:mm") ||
        (dateTimeType === "date" && "yyyy-MM-dd") ||
        (dateTimeType === "time" && "HH:mm") ||
        undefined
      }
      minDate={minDate ?? undefined}
      maxDate={maxDate ?? undefined}
      excludeDates={excludeDates}
      excludeDateIntervals={excludeDateIntervals}
      timeFormat="HH:mm"
      selected={selected}
      onChange={handleChange}
      placeholderText={dateTimeType === "dateTime" ? "Select date and time" : dateTimeType === "date" ? "Select date" : "Select time"}
    />
  );
}
